import * as XLSX from 'xlsx';
import { IDelayQuestions } from '../../../shared/types/questions';

interface ResponseData {
    data: IDelayQuestions[];
    total: number;
    filters: {
        companyId: string;
        startDate: string | null;
        endDate: string | null;
    };
    period: {
        start: string | null;
        end: string | null;
    };
}

// Função para formatar data e hora
const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

// Função para traduzir escolhas
const translateChoice = (choice: string | null | undefined): string => {
    if (!choice) return '';
    
    const translations: { [key: string]: string } = {
        'immediate': 'Imediato',
        'delayed': 'Atrasado',
        'Immediate': 'Imediato',
        'Delayed': 'Atrasado'
    };
    
    return translations[choice] || choice;
};

export const ExportDelayData = (response: ResponseData, fileName: string) => {
    try {
        // Dados principais
        const excelData = response.data.map((item) => ({
            'Record ID': item.record_id,
            'Nome': item.nome,
            'Data Nascimento': item.dtnasc,
            'Aplicador': item.applicatorName || 'N/A',
            'Valor Imediato': item.valor_imediato,
            'Valor Atrasado': item.valor_atrasado,
            'R1 Índice': item.r1_indice,
            'R1 Tempo (s)': item.r1_tempo,
            'R1 Escolha': translateChoice(item.r1_escolha),
            'R2 Índice': item.r2_indice,
            'R2 Tempo (s)': item.r2_tempo,
            'R2 Escolha': translateChoice(item.r2_escolha),
            'R3 Índice': item.r3_indice,
            'R3 Tempo (s)': item.r3_tempo,
            'R3 Escolha': translateChoice(item.r3_escolha),
            'R4 Índice': item.r4_indice,
            'R4 Tempo (s)': item.r4_tempo,
            'R4 Escolha': translateChoice(item.r4_escolha),
            'R5 Índice': item.r5_indice,
            'R5 Tempo (s)': item.r5_tempo,
            'R5 Escolha': translateChoice(item.r5_escolha),
            'ED50': item.ed50,
            'K': item.k,
            'Iniciado em': formatDateTime(item.startedAt),
            'Completado em': formatDateTime(item.completedAt),
            'Tempo Total (s)': item.totalTimeSeconds,
        }));

        // Aba de documentação simplificada
        const documentationData = [
            {
                'Campo': 'Record ID',
                'Descrição': 'Identificador único do registro no sistema, gerado automaticamente para cada participante'
            },
            {
                'Campo': 'Nome',
                'Descrição': 'Nome completo do participante'
            },
            {
                'Campo': 'Data Nascimento',
                'Descrição': 'Data de nascimento do participante (formato: DD/MM/AAAA ou AAAA-MM-DD)'
            },
            {
                'Campo': 'Aplicador',
                'Descrição': 'Nome do usuário que aplicou o teste'
            },
            {
                'Campo': 'Valor Imediato',
                'Descrição': 'Valor monetário (R$) da recompensa imediata apresentada nas escolhas'
            },
            {
                'Campo': 'Valor Atrasado',
                'Descrição': 'Valor monetário (R$) da recompensa atrasada apresentada nas escolhas'
            },
            {
                'Campo': 'R1 Índice',
                'Descrição': 'Índice da opção de atraso apresentada na Resposta 1 (varia de 1 a 31, conforme tabela de parâmetros)'
            },
            {
                'Campo': 'R1 Tempo (s)',
                'Descrição': 'Tempo em segundos que o participante levou para fazer a escolha na Resposta 1 (latência da resposta)'
            },
            {
                'Campo': 'R1 Escolha',
                'Descrição': 'Escolha feita pelo participante na Resposta 1 (valores: "Imediato" ou "Atrasado")'
            },
            {
                'Campo': 'R2 Índice',
                'Descrição': 'Índice da opção de atraso apresentada na Resposta 2 (ajustado baseado na escolha R1)'
            },
            {
                'Campo': 'R2 Tempo (s)',
                'Descrição': 'Tempo em segundos que o participante levou para fazer a escolha na Resposta 2 (latência da resposta)'
            },
            {
                'Campo': 'R2 Escolha',
                'Descrição': 'Escolha feita pelo participante na Resposta 2 (valores: "Imediato" ou "Atrasado")'
            },
            {
                'Campo': 'R3 Índice',
                'Descrição': 'Índice da opção de atraso apresentada na Resposta 3 (ajustado baseado nas escolhas R1 e R2)'
            },
            {
                'Campo': 'R3 Tempo (s)',
                'Descrição': 'Tempo em segundos que o participante levou para fazer a escolha na Resposta 3 (latência da resposta)'
            },
            {
                'Campo': 'R3 Escolha',
                'Descrição': 'Escolha feita pelo participante na Resposta 3 (valores: "Imediato" ou "Atrasado")'
            },
            {
                'Campo': 'R4 Índice',
                'Descrição': 'Índice da opção de atraso apresentada na Resposta 4 (ajustado baseado nas escolhas R1, R2 e R3)'
            },
            {
                'Campo': 'R4 Tempo (s)',
                'Descrição': 'Tempo em segundos que o participante levou para fazer a escolha na Resposta 4 (latência da resposta)'
            },
            {
                'Campo': 'R4 Escolha',
                'Descrição': 'Escolha feita pelo participante na Resposta 4 (valores: "Imediato" ou "Atrasado")'
            },
            {
                'Campo': 'R5 Índice',
                'Descrição': 'Índice da opção de atraso apresentada na Resposta 5 final (ajustado baseado em todas as escolhas anteriores)'
            },
            {
                'Campo': 'R5 Tempo (s)',
                'Descrição': 'Tempo em segundos que o participante levou para fazer a escolha na Resposta 5 (latência da resposta)'
            },
            {
                'Campo': 'R5 Escolha',
                'Descrição': 'Escolha feita pelo participante na Resposta 5 (valores: "Imediato" ou "Atrasado")'
            },
            {
                'Campo': 'ED50',
                'Descrição': 'Effective Delay 50% - Atraso efetivo (em dias) em que o participante é indiferente entre as opções. Ponto onde a recompensa atrasada tem 50% do valor da recompensa imediata.'
            },
            {
                'Campo': 'K',
                'Descrição': 'Parâmetro K da função hiperbólica de desconto temporal (V = A/(1+kD)). Taxa de desconto temporal - valores maiores indicam maior impulsividade/desconto.'
            },
            {
                'Campo': 'Iniciado em',
                'Descrição': 'Data e hora em que o participante iniciou a tarefa (formato: DD/MM/AAAA HH:MM:SS)'
            },
            {
                'Campo': 'Completado em',
                'Descrição': 'Data e hora em que o participante concluiu a tarefa (formato: DD/MM/AAAA HH:MM:SS)'
            },
            {
                'Campo': 'Tempo Total (s)',
                'Descrição': 'Tempo total em segundos para completar toda a tarefa (soma de todos os tempos de resposta mais intervalos entre telas)'
            }
        ];

        // Criar as planilhas
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const docWorksheet = XLSX.utils.json_to_sheet(documentationData);

        // Ajustar larguras das colunas - Aba de Dados
        const columnWidths = [
            { wch: 15 },  // Record ID
            { wch: 25 },  // Nome
            { wch: 18 },  // Data Nascimento
            { wch: 25 },  // Aplicador (novo campo)
            { wch: 15 },  // Valor Imediato
            { wch: 15 },  // Valor Atrasado
            { wch: 12 },  // R1 Índice
            { wch: 14 },  // R1 Tempo (s)
            { wch: 14 },  // R1 Escolha
            { wch: 12 },  // R2 Índice
            { wch: 14 },  // R2 Tempo (s)
            { wch: 14 },  // R2 Escolha
            { wch: 12 },  // R3 Índice
            { wch: 14 },  // R3 Tempo (s)
            { wch: 14 },  // R3 Escolha
            { wch: 12 },  // R4 Índice
            { wch: 14 },  // R4 Tempo (s)
            { wch: 14 },  // R4 Escolha
            { wch: 12 },  // R5 Índice
            { wch: 14 },  // R5 Tempo (s)
            { wch: 14 },  // R5 Escolha
            { wch: 15 },  // ED50
            { wch: 15 },  // K
            { wch: 22 },  // Iniciado em
            { wch: 22 },  // Completado em
            { wch: 16 },  // Tempo Total (s)
        ];
        worksheet['!cols'] = columnWidths;

        // Ajustar larguras das colunas - Aba de Documentação
        const docColumnWidths = [
            { wch: 20 },  // Campo
            { wch: 120 }, // Descrição
        ];
        docWorksheet['!cols'] = docColumnWidths;

        // Criar o workbook e adicionar as abas
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Dados');
        XLSX.utils.book_append_sheet(workbook, docWorksheet, 'Documentação');

        // Criar nome do arquivo com período
        const periodInfo = response.period.start && response.period.end 
            ? `_${response.period.start.split('T')[0]}_ate_${response.period.end.split('T')[0]}`
            : `_${new Date().toISOString().split('T')[0]}`;
        
        const fileNameWithDate = `${fileName}${periodInfo}.xlsx`;
        
        XLSX.writeFile(workbook, fileNameWithDate);
        
        console.log(`✅ Relatório Excel exportado com sucesso: ${fileNameWithDate}`);
        console.log(`📊 Total de registros exportados: ${response.total}`);
        console.log(`📄 Abas criadas: Dados e Documentação`);
        
        return true;
        
    } catch (error) {
        console.error('❌ Erro ao exportar dados para Excel:', error);
        throw error;
    }
};